/**
 * SchemaDetailCard Component
 *
 * Phase 3.2: Detailed view of a single schema/data source.
 * Shows fields grouped by type with action buttons.
 *
 * Created: January 2026
 */

import React, { useState } from 'react';
import {
  Database,
  Hash,
  Calendar,
  Type,
  Zap,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  X,
} from 'lucide-react';
import {
  SchemaExplorationResult,
  SchemaFieldInfo,
} from '../../types/chartDataSource';

interface SchemaDetailCardProps {
  explorationResult: SchemaExplorationResult;
  onUseSchema: (schemaId: string) => void;
  onSampleQuery: (query: string) => void;
  onBack: () => void;
  hasSelectedChart?: boolean;
  onClose?: () => void;
}

const FieldTypeIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'number':
      return <Hash className="h-3.5 w-3.5 text-blue-500" />;
    case 'date':
    case 'datetime':
      return <Calendar className="h-3.5 w-3.5 text-purple-500" />;
    case 'boolean':
      return <Zap className="h-3.5 w-3.5 text-yellow-500" />;
    default:
      return <Type className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />;
  }
};

export function SchemaDetailCard({
  explorationResult,
  onUseSchema,
  onSampleQuery,
  onBack,
  hasSelectedChart = false,
  onClose,
}: SchemaDetailCardProps) {
  const { schema, chartableFields, timeFields, otherFields, sampleQueries } =
    explorationResult;
  const [showAllFields, setShowAllFields] = useState(false);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-emerald-50 dark:bg-emerald-900/20 px-4 py-3 border-b border-emerald-100 dark:border-emerald-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-emerald-600" />
            <span className="font-medium text-emerald-900 dark:text-emerald-200">
              {schema.displayName || schema.name}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onBack}
              className="text-sm text-emerald-600 hover:text-emerald-700 dark:hover:text-emerald-400 flex items-center gap-1"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to list
            </button>
            {onClose && (
              <button
                onClick={onClose}
                className="text-emerald-600 hover:text-emerald-800 dark:hover:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded p-0.5 transition-colors"
                aria-label="Close schema detail"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        {schema.description && (
          <p className="text-sm text-emerald-700 dark:text-emerald-400 mt-1">{schema.description}</p>
        )}
      </div>

      {/* Chartable Fields */}
      {chartableFields.length > 0 && (
        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <Hash className="h-4 w-4 text-blue-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Chartable Fields ({chartableFields.length})
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {chartableFields.map((field) => (
              <div
                key={field.name}
                className="px-2 py-1 bg-blue-50 dark:bg-blue-900/20 rounded text-sm text-blue-700 dark:text-blue-400 flex items-center gap-1"
                title={field.description}
              >
                <FieldTypeIcon type={field.type} />
                {field.displayName || field.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Time Fields */}
      {timeFields.length > 0 && (
        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-4 w-4 text-purple-500" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Time Fields ({timeFields.length})
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {timeFields.map((field) => (
              <div
                key={field.name}
                className="px-2 py-1 bg-purple-50 dark:bg-purple-900/20 rounded text-sm text-purple-700 dark:text-purple-400 flex items-center gap-1"
                title={field.description}
              >
                <FieldTypeIcon type={field.type} />
                {field.displayName || field.name}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Other Fields (collapsible) */}
      {otherFields.length > 0 && (
        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
          <button
            onClick={() => setShowAllFields(!showAllFields)}
            className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            {showAllFields ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            Other Fields ({otherFields.length})
          </button>
          {showAllFields && (
            <div className="flex flex-wrap gap-2 mt-2">
              {otherFields.map((field) => (
                <div
                  key={field.name}
                  className="px-2 py-1 bg-gray-50 dark:bg-gray-800/50 rounded text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1"
                  title={field.description}
                >
                  <FieldTypeIcon type={field.type} />
                  {field.displayName || field.name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sample Queries */}
      {sampleQueries.length > 0 && (
        <div className="p-4 border-b border-gray-100 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-medium">
            Example Queries
          </span>
          <div className="mt-2 space-y-1">
            {sampleQueries.map((query, i) => (
              <button
                key={i}
                onClick={() => onSampleQuery(query)}
                className="block w-full text-left px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 hover:text-emerald-700 dark:hover:text-emerald-400 rounded-lg transition-colors"
              >
                "{query}"
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Action Button */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50">
        <button
          onClick={() => onUseSchema(schema.id ?? schema.name)}
          className="w-full py-2 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors"
        >
          {hasSelectedChart
            ? 'Connect to Selected Chart'
            : 'Create Chart with This Data'}
        </button>
      </div>
    </div>
  );
}

export default SchemaDetailCard;
